export const PRICE_SCALE = 10000;
export const CRYPTO_SCALE = 100000000;
export const BRL_CENTS_SCALE = 100;

const PRICE_SCALE_BI = 10000n;
const CRYPTO_SCALE_BI = 100000000n;
const BRL_CENTS_SCALE_BI = 100n;

const toScaleBigInt = (scale) => BigInt(scale);

const countScaleDigits = (scale) => {
  let digits = 0;
  let value = toScaleBigInt(scale);
  while (value > 1n) {
    if (value % 10n !== 0n) return digits;
    value /= 10n;
    digits += 1;
  }
  return digits;
};

/** Parses FoxBit decimal string into fixed-point BigInt at the given scale. */
export const parseFoxBitDecimal = (value, scale) => {
  const scaleBig = toScaleBigInt(scale);
  const str = String(value ?? "").trim();
  if (!str) return 0n;

  const negative = str.startsWith("-");
  const abs = negative ? str.slice(1) : str;
  const [intPartRaw = "0", fracPartRaw = ""] = abs.split(".");
  const intPart = intPartRaw.replace(/^0+(?=\d)/, "") || "0";
  const scaleDigits = countScaleDigits(scale);
  const fracPart = fracPartRaw.slice(0, scaleDigits).padEnd(scaleDigits, "0");
  const combined = `${intPart}${fracPart}`.replace(/^0+(?=\d)/, "") || "0";
  const parsed = BigInt(combined);
  return negative ? -parsed : parsed;
};

const formatScaledDecimal = (scaled, scale, decimals) => {
  const negative = scaled < 0n;
  const abs = negative ? -scaled : scaled;
  const scaleDigits = countScaleDigits(scale);
  const digits = abs.toString().padStart(scaleDigits + 1, "0");
  const intPart = digits.slice(0, -scaleDigits) || "0";
  const fracPart = digits.slice(-scaleDigits).padEnd(decimals, "0").slice(0, decimals);
  const numeric = Number(`${intPart}.${fracPart}`);
  return new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(negative ? -numeric : numeric);
};

/** Formats a FoxBit BRL price/amount string for display. */
export const formatBrlFromString = (value, decimals = 4) => {
  const str = String(value ?? "").trim();
  if (!str) {
    return new Intl.NumberFormat("pt-BR", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(0);
  }

  const negative = str.startsWith("-");
  const abs = negative ? str.slice(1) : str;
  const [intPartRaw = "0", fracPartRaw = ""] = abs.split(".");
  const intPart = intPartRaw.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  const fracPart = fracPartRaw.padEnd(decimals, "0").slice(0, decimals);
  return `${negative ? "-" : ""}${intPart},${fracPart}`;
};

export const CRYPTO_DISPLAY_DECIMALS = 6;

/** Formats a numeric crypto amount: dot decimal, fixed decimals, no thousand separators. */
export const formatCryptoAmount = (value, decimals = CRYPTO_DISPLAY_DECIMALS) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return `0.${"0".repeat(decimals)}`;
  }

  const negative = numericValue < 0;
  const abs = Math.abs(numericValue);
  const [intPart, fracPart = ""] = abs.toFixed(decimals).split(".");
  return `${negative ? "-" : ""}${intPart}.${fracPart}`;
};

/** Formats a FoxBit crypto amount string for display (dot decimal, fixed decimals). */
export const formatCryptoFromString = (value, decimals = CRYPTO_DISPLAY_DECIMALS) => {
  const str = String(value ?? "").trim();
  if (!str) return `0.${"0".repeat(decimals)}`;

  const negative = str.startsWith("-");
  const abs = negative ? str.slice(1) : str;
  const [intPartRaw = "0", fracPartRaw = ""] = abs.split(".");
  const intPart = intPartRaw.replace(/^0+(?=\d)/, "") || "0";
  const fracPart = fracPartRaw.padEnd(decimals, "0").slice(0, decimals);
  return `${negative ? "-" : ""}${intPart}.${fracPart}`;
};

/** brlCents: BRL amount in centavos (BigInt or number). priceString: FoxBit unit price. */
export const calcCryptoFromBrl = (brlCentsOrScaled, priceString) => {
  const brlCents = BigInt(brlCentsOrScaled);
  const priceScaled = parseFoxBitDecimal(priceString, PRICE_SCALE);
  if (priceScaled === 0n || brlCents <= 0n) return 0n;

  return (brlCents * CRYPTO_SCALE_BI * PRICE_SCALE_BI) / (priceScaled * BRL_CENTS_SCALE_BI);
};

/** cryptoScaled: crypto amount at CRYPTO_SCALE. Returns BRL centavos. */
export const calcBrlFromCrypto = (cryptoScaled, priceString) => {
  const crypto = BigInt(cryptoScaled);
  const priceScaled = parseFoxBitDecimal(priceString, PRICE_SCALE);
  if (priceScaled === 0n || crypto <= 0n) return 0n;

  return (crypto * priceScaled * BRL_CENTS_SCALE_BI) / (CRYPTO_SCALE_BI * PRICE_SCALE_BI);
};

export const cryptoScaledToNumber = (cryptoScaled) =>
  Number(BigInt(cryptoScaled)) / CRYPTO_SCALE;

export const brlCentsToNumber = (brlCents) =>
  Number(BigInt(brlCents)) / BRL_CENTS_SCALE;

export const isPositiveDecimalString = (value) => {
  const str = String(value ?? "").trim();
  if (!str) return false;
  const numeric = Number(str);
  return Number.isFinite(numeric) && numeric > 0;
};
